
<main class="min-h-screen w-full text-slate-200 p-4 sm:p-6 md:p-8 flex flex-col antialiased">

  @if (!selectedMode()) {
    <div class="w-full max-w-4xl mx-auto flex-grow flex flex-col justify-center">
      <header class="text-center mb-8">
        <h1 class="text-4xl sm:text-5xl font-bold text-cyan-300">深呼吸練習器</h1>
        <p class="text-slate-400 mt-2 text-lg">選擇一個練習來開始放鬆與專注</p>
      </header>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        @for (mode of modes(); track mode.id) {
          <div class="bg-slate-800/50 rounded-xl p-6 border border-slate-700 hover:border-cyan-400 transition-all duration-300 flex flex-col">
            <h2 class="text-2xl font-semibold text-white mb-2">{{ mode.name }}</h2>
            <p class="text-cyan-400 font-mono mb-3 text-lg">{{ mode.slogan }}</p>
            <p class="text-slate-300 text-base mb-4 flex-grow">{{ mode.description }}</p>

            <div class="mb-4">
              <h3 class="font-semibold text-slate-100 mb-2">步驟：</h3>
              <ul class="list-none space-y-1 text-slate-600">
                @for (step of mode.steps; track $index) {
                  <li class="flex items-center">
                    <svg class="w-4 h-4 mr-2 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                    {{ step }}
                  </li>
                }
              </ul>
            </div>

            <div class="mb-5">
              <h3 class="font-semibold text-slate-600 mb-2">適用：</h3>
              <p class="text-slate-700">{{ mode.useCase }}</p>
            </div>
            @if (mode.warning) {
              <div class="bg-amber-900/50 border border-amber-700 text-amber-200 text-sm rounded-md p-3">
                <strong>注意：</strong> {{ mode.warning }}
              </div>
            }
            <button (click)="selectMode(mode)" class="mt-auto w-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-300">
              選擇此模式
            </button>
          </div>
        }
      </div>
    </div>
  } @else {
    <div class="w-full max-w-4xl mx-auto flex-grow flex flex-col items-center justify-center relative">
      <button (click)="goBack()" class="absolute top-0 left-0 flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
        返回
      </button>

      <button (click)="toggleMute()" class="absolute top-0 right-0 p-2 text-slate-400 hover:text-white transition-colors">
        @if (isMuted()) {
          <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM12.293 7.293a1 1 0 011.414 0L15 8.586l1.293-1.293a1 1 0 111.414 1.414L16.414 10l1.293 1.293a1 1 0 01-1.414 1.414L15 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L13.586 10l-1.293-1.293a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        } @else {
          <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414z" clip-rule="evenodd" />
            <path d="M16.5 10a5.985 5.985 0 01-1.757 4.243 1 1 0 01-1.414-1.414A3.986 3.986 0 0015 10c0-1.105-.448-2.105-1.172-2.828a1 1 0 011.414-1.414A5.985 5.985 0 0116.5 10z" />
          </svg>
        }
      </button>

      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-white">{{ selectedMode()?.name }}</h1>
        <p class="text-cyan-400 font-mono mt-1 text-lg">{{ selectedMode()?.slogan }}</p>
      </div>

      <div class="relative w-64 h-64 sm:w-80 sm:h-80 md:w-96 md:h-96 flex items-center justify-center">
        <div class="absolute inset-0 bg-slate-800 rounded-full"></div>
        <div
          class="absolute inset-0 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full transition-transform ease-in-out"
          [style.transition-duration]="isRunning() ? (currentPhase()?.duration ?? 1) + 's' : '0.5s'"
          [class.scale-100]="animationState() === 'idle' || animationState() === 'exhale'"
          [class.scale-150]="animationState() === 'inhale'"
          [class.scale-150]="isHoldAfterInhale()"
          [class.scale-100]="isHoldAfterExhale()"
          >
        </div>

        <div class="relative z-10 text-center">
          <p class="text-5xl sm:text-6xl font-bold text-white tracking-tighter">{{ phaseInstruction() }}</p>
          @if (isRunning()) {
            <p class="text-7xl sm:text-8xl font-mono font-light text-white/80 mt-2">{{ countdown() }}</p>
          }
        </div>
      </div>

      <div class="mt-12 text-center">
        <button (click)="toggleExercise()" class="w-48 text-2xl font-semibold py-4 px-8 rounded-full transition-all duration-300"
          [class.bg-cyan-600]="!isRunning()"
          [class.hover:bg-cyan-500]="!isRunning()"
          [class.text-white]="!isRunning()"
          [class.bg-rose-700]="isRunning()"
          [class.hover:bg-rose-600]="isRunning()"
          [class.text-white]="isRunning()">
          {{ isRunning() ? '停止' : '開始' }}
        </button>
      </div>
    </div>
  }
</main>
